import type { ServiceType } from './service-types';

/**
 * Scheduled service checks (Phase 04; ARCHITECTURE_CORRECTION_PLAN §H; docs/integrations/).
 *
 * Only checks that need no credentials and no server changes exist. Each kind belongs to exactly
 * one service type. Every kind is a service-level check (it talks to the service itself), never a
 * bare host ping.
 */
export const CHECK_KINDS = [
  'website_https',
  'nextcloud_status',
  'smtp_tls',
  'imap_tls',
  'mail_dns',
] as const;
export type CheckKind = (typeof CHECK_KINDS)[number];

/** The service type each check kind applies to. */
export const CHECK_KIND_SERVICE_TYPE: Record<CheckKind, ServiceType> = {
  website_https: 'website',
  nextcloud_status: 'cloud_server',
  smtp_tls: 'mail_server',
  imap_tls: 'mail_server',
  mail_dns: 'mail_server',
};

/**
 * What each check proves. The mail checks prove the protocol service answers over TLS; they are
 * NOT proof that mail is delivered.
 */
export const CHECK_KIND_DESCRIPTIONS: Record<CheckKind, string> = {
  website_https: 'Website HTTPS availability: response status, response time, TLS certificate',
  nextcloud_status: 'Cloud Server application status (status.php), TLS certificate',
  smtp_tls:
    'Mail sending service (SMTP) availability: TLS handshake and greeting. Not proof of delivery.',
  imap_tls:
    'Mailbox access service (IMAP) availability: TLS handshake and greeting. Not proof of delivery.',
  mail_dns:
    'Mail domain DNS: MX, SPF, DMARC and DKIM records published, and the mail server’s reverse DNS matches its name. Affects delivery; not proof of delivery.',
};

/**
 * Outcome of one check run:
 *  - ok:      the service answered correctly.
 *  - warning: it answered correctly, but something needs attention soon (TLS certificate expiry).
 *  - failed:  it did not answer correctly (timeout, refused, TLS error, bad status, maintenance...).
 */
export const CHECK_STATUSES = ['ok', 'warning', 'failed'] as const;
export type CheckStatus = (typeof CHECK_STATUSES)[number];

/** Stable, non-secret failure codes recorded with a failed or warning result. */
export const CHECK_ERROR_CODES = [
  'timeout',
  'dns_error',
  'connection_refused',
  'connection_error',
  'tls_error',
  'tls_certificate_expired',
  'tls_certificate_expiring',
  'http_status',
  'too_many_redirects',
  'invalid_redirect',
  'unexpected_response',
  'unexpected_greeting',
  'starttls_unavailable',
  'maintenance_mode',
  'needs_db_upgrade',
  'not_installed',
  'target_not_configured',
  'target_not_allowed',
  'dns_record_missing',
  'reverse_dns_mismatch',
] as const;
export type CheckErrorCode = (typeof CHECK_ERROR_CODES)[number];

const DAY_MS = 86_400_000;

/**
 * Technical defaults for scheduled checks. The TLS warning window is a conservative technical
 * default (documented in docs/integrations/README.md), not a business rule.
 */
export const MONITORING = {
  /** Whole-check deadline: connect, TLS, request and response together. */
  probeTimeoutMs: 10_000,
  defaultIntervalSeconds: 300,
  minIntervalSeconds: 60,
  maxIntervalSeconds: 86_400,
  /** A result is stale once this many runs were missed, plus the grace period. */
  staleAfterMissedRuns: 2,
  staleGraceSeconds: 120,
  tlsExpiryWarningDays: 14,
  maxRedirects: 5,
} as const;

/**
 * A check result is stale when no newer result arrived within the expected window. Stale results
 * are never presented as current. Derived from stored timestamps at read time, so it cannot
 * itself go out of date.
 *
 * The window runs from the later of the last result and the last configuration change
 * (`configuredAt`), so changing a check's interval does not by itself make a recent result stale;
 * the rescheduled run then has a full window to report.
 */
export function isCheckStale(
  lastCheckedAt: Date | null,
  intervalSeconds: number,
  now: Date,
  configuredAt: Date | null = null,
) {
  if (lastCheckedAt === null) return false;
  const windowMs =
    (intervalSeconds * MONITORING.staleAfterMissedRuns + MONITORING.staleGraceSeconds) * 1000;
  const since = configuredAt && configuredAt > lastCheckedAt ? configuredAt : lastCheckedAt;
  return now.getTime() - since.getTime() > windowMs;
}

export type CertificateExpiry = 'valid' | 'expiring_soon' | 'expired';

export function certificateExpiry(validTo: Date, now: Date): CertificateExpiry {
  const remaining = validTo.getTime() - now.getTime();
  if (remaining <= 0) return 'expired';
  if (remaining <= MONITORING.tlsExpiryWarningDays * DAY_MS) return 'expiring_soon';
  return 'valid';
}

/**
 * Raw check-result retention (Phase 07 Stage 04; DECISIONS.md A-RT1). A fixed operational policy,
 * not a setting. The 90-day period and the rule that each check keeps its newest result are
 * enforced by the database function `prune_monitoring_check_results` (migration 0011), which
 * takes no cutoff from its caller; `days` here only documents it for tests. The worker removes
 * at most `batchSize` rows per call and `maxBatchesPerRun` calls per daily run.
 */
export const RESULT_RETENTION = {
  days: 90,
  batchSize: 5_000,
  maxBatchesPerRun: 200,
} as const;
